import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PROFILE_SUBPAGE_SLIDE_MS } from "./profileSubpageNav";
import "./ProfileSubpageShell.css";

const ProfileSubpageBackContext = createContext(null);

export function useProfileSubpageBack(fallbackPath = "/profile-details") {
  const context = useContext(ProfileSubpageBackContext);
  const navigate = useNavigate();

  return useCallback(() => {
    if (context?.requestBack) {
      context.requestBack();
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(fallbackPath, { replace: true, state: { instantTab: true } });
  }, [context, fallbackPath, navigate]);
}

export default function ProfileSubpageShell({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const shouldAnimate = Boolean(location.state?.subpageSlide);
  const [enterReady, setEnterReady] = useState(!shouldAnimate);
  const [isExiting, setIsExiting] = useState(false);
  const [exitReady, setExitReady] = useState(false);

  useLayoutEffect(() => {
    if (!shouldAnimate) {
      setEnterReady(true);
      return undefined;
    }

    setEnterReady(false);
    setIsExiting(false);
    setExitReady(false);

    let enterFrame = 0;
    const startFrame = requestAnimationFrame(() => {
      enterFrame = requestAnimationFrame(() => {
        setEnterReady(true);
      });
    });

    return () => {
      cancelAnimationFrame(startFrame);
      cancelAnimationFrame(enterFrame);
    };
  }, [location.pathname, shouldAnimate]);

  const requestBack = useCallback(() => {
    if (isExiting) return;

    if (!shouldAnimate) {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/profile-details", { replace: true, state: { instantTab: true } });
      }
      return;
    }

    setIsExiting(true);
    setExitReady(false);

    let exitFrame = 0;
    const startFrame = requestAnimationFrame(() => {
      exitFrame = requestAnimationFrame(() => {
        setExitReady(true);
      });
    });

    window.setTimeout(() => {
      cancelAnimationFrame(startFrame);
      cancelAnimationFrame(exitFrame);
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/profile-details", { replace: true, state: { instantTab: true } });
      }
    }, PROFILE_SUBPAGE_SLIDE_MS);
  }, [isExiting, navigate, shouldAnimate]);

  const shellClass = useMemo(() => {
    if (!shouldAnimate) return "profile-subpage-shell profile-subpage-shell--steady";

    if (isExiting) {
      return exitReady
        ? "profile-subpage-shell profile-subpage-shell--exit"
        : "profile-subpage-shell profile-subpage-shell--exit-pending";
    }

    return enterReady
      ? "profile-subpage-shell profile-subpage-shell--enter"
      : "profile-subpage-shell profile-subpage-shell--enter-pending";
  }, [enterReady, exitReady, isExiting, shouldAnimate]);

  const value = useMemo(() => ({ requestBack }), [requestBack]);

  return (
    <ProfileSubpageBackContext.Provider value={value}>
      <div className={shellClass}>{children}</div>
    </ProfileSubpageBackContext.Provider>
  );
}
